import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { createSearchParams, useNavigate } from 'react-router-dom';
import AMLAddressHeader from '../../components/AMLAddressHeader/ui/AMLAddressHeader';
import AMLDetailList from '../../components/AMLDetailsList';
import AMLFundsRiskItem from '../../components/AMLFundsRiskItem/AMLFundsRiskItem';
import Button from '../../components/Button';
import Copy from '../../components/Copy';
import Icon, { Icons } from '../../components/Icon/Icon';
import ListItem from '../../components/ListItem';
import PageHeader from '../../components/PageHeader';
import { CurrencyItemWithSkeleton, ListItemTextWithSkeleton } from '../../components/Skeletons/ItemsWithSkeleton';
import StickyPanel from '../../components/StickyPanel';
import SlidingTabs from '../../components/slidingTabs/SlidingTabs';
import AppConfig from '../../config';
import { usePageContext } from '../../hooks/usePageContext';
import { t } from '../../lang';
import HelperService from '../../services/HelperService';
import RequestService from '../../services/RequestService';
import TypesService from '../../services/TypesService';
import { appSlice } from '../../store/slices';
import './AddressAmlDetails.scss';
import AMLInsightBlock from './components/AMLInsightBlock';

const SIZE_CHEVRON = 8;
const { routes } = AppConfig;

const BlockchainsById = TypesService.BlockchainsById;

const AddressAmlDetails = () => {
	const intl = useIntl();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { useSearchParams } = usePageContext();

	const [loading, setLoading] = useState(true);
	const [details, setDetails] = useState(null);
	const [blockchain, setBlockchain] = useState(null);
	const [currencyBlock, setCurrencyBlock] = useState(null);

	const currencyId = Number(useSearchParams().get('currencyId'));
	const address = useSearchParams().get('address');
	const addressAmlId = Number(useSearchParams().get('addressAmlId'));

	const getDetailsAML = useCallback(async () => {
		const res = addressAmlId
			? await RequestService.GetAddressAmlDetailsById(addressAmlId)
			: await RequestService.GetAddressAmlDetailsByAddress(address);

		setDetails(res.AddressAmlDetails);

		const resAddress =
			res?.AddressAmlDetails?.Balances?.length > 0
				? currencyId
					? res.AddressAmlDetails.Balances.find(i => i.CurrencyId === currencyId)
					: res.AddressAmlDetails.Balances[0]
				: null;

		setBlockchain(Object.values(BlockchainsById).find(i => i.network === res.AddressAmlDetails.Blockchain));

		resAddress !== null &&
			setCurrencyBlock([
				{
					Amount: resAddress?.Amount,
					AmountUsd: resAddress?.AmountUsd,
					Id: resAddress?.CurrencyId,
					Label: resAddress?.Label,
					BlockchainId: res.AddressAmlDetails.Blockchain,
				},
			]);

		setLoading(false);
	}, [address]);

	useEffect(() => {
		(addressAmlId || address) && getDetailsAML();
	}, [getDetailsAML, address, addressAmlId]);

	const openHighRiskModal = () => {
		dispatch(
			appSlice.actions.addModal({
				headClass: 'AmlHighRisk',
				isHeaderWithColorBgAndIcon: true,
				title: `${intl.formatMessage({ id: 'High risk' })} %`,
				subtitle: t('Part of the funds associated with high-risk sources.'),
				headerBackgroundColor: 'rgb(var(--AML-high))',
				headerIcon: 'DefaultAML',
				tag: 'AmlHighRisk',
				isOpen: true,
				height: '70%',
			})
		);
	};

	const openModalCurrencyList = () => {
		dispatch(
			appSlice.actions.addModal({
				title: t('All balance'),
				tag: 'ModalCurrencyList',
				isOpen: true,
				height: '70%',
				outsideParams: {
					items: details.Balances.map(i => ({
						Amount: i.Amount,
						AmountUsd: i.AmountUsd,
						Id: i.CurrencyId,
					})),
				},
			})
		);
	};

	const openAmlCheck = () =>
		navigate({
			pathname: routes.amlCheck,
			search: createSearchParams({ address }).toString(),
		});

	return (
		<>
			<PageHeader collapsed={true} header={'AML Details'} />
			<StickyPanel
				controls={
					<Button
						onClick={openAmlCheck}
						iconBefore='ARROW_CLOCKWISE'
						styleType='green-dark'
						className='AddressAmlDetails__recheck'
					>
						{t('Recheck address')}
					</Button>
				}
			>
				<div className='AddressAmlDetails'>
					<AMLAddressHeader
						percent={details?.RiskScore}
						isActualAML={details?.IsActual}
						lastCheckDate={details?.CheckDate}
						blockchainName={details?.Blockchain}
						isLoading={details?.Status === 'None' || details?.Status === 'Pending' || loading}
					/>
					<div className='AddressAmlDetails__top'>
						<div
							className={classNames('AddressAmlDetails__high-risk FormBlock', {
								'AddressAmlDetails__high-risk-red': details?.HighRisk > 0,
							})}
						>
							<ListItemTextWithSkeleton
								title={`${intl.formatMessage({ id: 'High risk' })} %`}
								description={t('Part of the funds associated with high-risk sources.')}
								rightTitle={(details?.HighRisk || 0) + '%'}
								trailing={[<Icon type={Icons.CHEVRON} width={SIZE_CHEVRON} />]}
								onSelect={openHighRiskModal}
								loading={loading}
								items={[1]}
								skeletonCount={1}
							/>
						</div>

						<div
							className={classNames('AddressAmlDetails__balances FormBlock', {
								multipleBalances: details?.Balances.length > 1,
							})}
						>
							{currencyBlock || loading ? (
								<CurrencyItemWithSkeleton loading={loading} items={currencyBlock} skeletonCount={1} />
							) : (
								<ListItem
									className='CurrencyItem'
									icon={blockchain?.Icon}
									title={blockchain?.network}
									bottomSeparator={false}
								/>
							)}

							<ListItem
								className='multipleBalances-btn'
								title={t('All balance')}
								trailing={[<Icon type={Icons.CHEVRON} width={SIZE_CHEVRON} />]}
								onSelect={openModalCurrencyList}
							/>
						</div>

						<Copy
							loading={loading}
							title={currencyBlock?.[0]?.Label || intl.formatMessage({ id: 'My address' })}
							description={details?.Address || ''}
							copyText={details?.Address || ''}
						/>

						<div className='AddressAmlDetails__transactions FormBlock'>
							<ListItemTextWithSkeleton
								title={t('Transactions')}
								rightTitle={details?.TransactionCount || 0}
								loading={loading}
								items={[1]}
								skeletonCount={1}
							/>
						</div>
					</div>

					<div className='AddressAmlDetails__connections-title'>{t('All connections')}</div>

					<div className='AddressAmlDetails__connections-funds'>
						<AMLFundsRiskItem
							percent={details?.RiskScore ? details?.CounterpartyDetails.Received.TotalRiskScore : null}
							currencyId={currencyId}
							isActualAML={details?.IsActual}
							checkDate={details?.CheckDate}
						/>
						<AMLFundsRiskItem
							percent={details?.RiskScore ? details?.CounterpartyDetails.Sent.TotalRiskScore : null}
							currencyId={currencyId}
							isActualAML={details?.IsActual}
							checkDate={details?.CheckDate}
							type='use'
						/>
					</div>

					{details && <AMLInsightBlock details={details} />}

					{details && (
						<SlidingTabs
							tabsArray={[
								{
									label: t('Source of funds'),
									content: (
										<AMLDetailList
											amlDetailsListHeader={[t('Category'), t('Share V2'), t('Balance')]}
											amlDetailsList={HelperService.prepareSignalsCounterpartiesAml(
												details.CounterpartyDetails.Received.SignalCounterparties
											)}
											canRowClick
										/>
									),
								},
								{
									label: t('Use of funds'),
									content: (
										<AMLDetailList
											amlDetailsListHeader={[t('Category'), t('Share V2'), t('Balance')]}
											amlDetailsList={HelperService.prepareSignalsCounterpartiesAml(
												details.CounterpartyDetails.Sent.SignalCounterparties
											)}
											canRowClick
										/>
									),
								},
							]}
						/>
					)}
				</div>
			</StickyPanel>
		</>
	);
};

export default AddressAmlDetails;
